import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  ListSubheader,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import { VariableSizeList } from 'react-window'
import { matchSorter } from 'match-sorter'
import { chain as flatMap } from 'ramda'
import useAuth from 'src/hooks/useAuth'
import { useManageUser } from 'src/api/user/manage'
import ClickAwayTooltip from './ClickAwayTooltip'
import Chip from '@mui/material/Chip'
import ClearIcon from '@mui/icons-material/Clear'
import { IconButton } from '@mui/material'
import { HPO_DETAILED_DATA } from 'src/constants'

const LISTBOX_PADDING = 8 // px

function renderRow(props) {
  const { data, index, style } = props
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  })
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data) {
  const ref = React.useRef(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props
  const itemData = React.Children.toArray(children)
  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})
ListboxComponent.propTypes = {
  children: PropTypes.node,
}

// Parent component will give a value list and its setter
// If component has saving to backend, it will be done in parent component.
// Timestamps will be done when the user saves it so adding user names and time is in parent component
// Some components require the deletion to also remove data from DB which is done in this component
function Tags({
  options,
  title,
  value,
  onChange,
  freeSolo,
  sortKeys = ['value', 'label'],
  deleteFromDB = null,
  association = null,
  ...rest
}) {
  const [inputValue, setInputValue] = useState('')
  // Deletion states
  const [selectedChip, setSelectedChip] = useState(null)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openClearAllModal, setOpenClearAllModal] = useState(false)
  // User data
  const { user } = useAuth()
  const { self, updateMutation } = useManageUser(user)
  const userDetails = self.data

  const filterOptions = useCallback(
    (options, { inputValue }) => {
      const matches = matchSorter(options, inputValue, { keys: sortKeys })
      if (/*matches.length === 0 &&*/ freeSolo && inputValue !== '') {
        return [{ label: `Add "${inputValue}"`, value: inputValue, freeSolo: true }, ...matches]
      }
      return matches
    },
    [freeSolo],
  )

  const handleChange = (_event, newValueList) => {
    const previousValues = value || []

    const newValuesFixed = newValueList
      .map((newValue) => {
        const isExisting = previousValues.some((prevValue) => prevValue.value === newValue.value)

        if (isExisting) {
          // If the tag was previously selected, keep the old data
          return newValue
        } else {
          // If it's a new tag, add the user data and timestamp
          return {
            label: newValue.label,
            value: newValue.value,
            firstName: userDetails?.first_name,
            lastName: userDetails?.last_name,
            timestamp: new Date().toISOString(),
            association: association,
          }
        }
      })
      .filter(Boolean)

    onChange(newValuesFixed)
  }

  // Clearing only one item/chip
  const handleDelete = (chip) => {
    setSelectedChip(chip)
    setOpenDeleteModal(true)
  }

  const handleClose = () => {
    setOpenDeleteModal(false)
    setSelectedChip(null)
  }

  const handleDeleteConfirm = async () => {
    const newValues = value.filter((item) => item.value !== selectedChip.value)
    onChange(newValues)
    if (deleteFromDB) {
      await deleteFromDB(newValues)
    }
    handleClose()
  }

  // For clearing all options
  const handleClearClick = () => {
    setOpenClearAllModal(true)
  }

  const handleConfirmClearClose = () => {
    setOpenClearAllModal(false)
  }

  const handleConfirmClear = async () => {
    onChange([])
    if (deleteFromDB) {
      await deleteFromDB([])
    }
    handleConfirmClearClose()
  }

  useEffect(() => {
    setInputValue(value?.label ?? '')
  }, [value])

  return (
    <>
      <Autocomplete
        multiple
        selectOnFocus
        clearOnBlur
        freeSolo={freeSolo}
        ListboxComponent={ListboxComponent}
        inputValue={inputValue}
        value={value || []}
        onChange={handleChange}
        filterOptions={filterOptions}
        options={options}
        renderInput={(params) => (
          <TextField {...params} onChange={(e) => setInputValue(e.target.value)} label={title} variant="outlined" />
        )}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        renderOption={(props, option, state) => {
          const matches = match(option.label, state.inputValue)
          const parts = parse(option.label, matches)

          return (
            <li {...props}>
              {parts.map((part, index) => (
                <Typography
                  key={index}
                  noWrap
                  style={{ fontWeight: part.highlight ? 800 : 500, whiteSpace: 'pre' }}
                  component="span"
                >
                  {part.text}
                </Typography>
              ))}
            </li>
          )
        }}
        renderTags={(value, getTagProps) => (
          <Box
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              p: 0.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}
          >
            <TagChip value={value} getTagProps={getTagProps} handleDelete={handleDelete} />
          </Box>
        )}
        clearIcon={
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              handleClearClick()
            }}
            sx={{
              padding: 0,
              margin: 0,
            }}
          >
            <ClearIcon />
          </IconButton>
        }
        {...rest}
      />
      <Dialog open={openDeleteModal} onClose={handleClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this item?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openClearAllModal} onClose={handleConfirmClearClose}>
        <DialogTitle>Confirm Clear All</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to clear all items?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClearClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClear} color="primary" autoFocus>
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

Tags.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  deleteFromDB: PropTypes.func,
}

const TagChip = ({ value, getTagProps, handleDelete }) => {
  const getDetailsForLabel = (id) => {
    const item = HPO_DETAILED_DATA.find((data) => {
      const nameWithId = `${data.name} (${data.id})`
      return nameWithId === id
    })
    if (item) {
      const { def, comment, synonyms } = item
      return {
        def: def || null,
        comment: comment || null,
        synonyms: synonyms && synonyms.length ? synonyms.join(', ') : null,
      }
    }
    return { def: null, comment: null, synonyms: null }
  }

  return (
    <>
      {value.map((option, index) => {
        const { def, comment, synonyms } = getDetailsForLabel(option.label)
        const timestamp = new Date(option.timestamp).toLocaleString()
        const hasAssociation = option.association ? true : false

        // Construct tooltip content
        const tooltipContent = (
          <Box>
            <Typography variant="body2">
              <strong>User:</strong> {option.firstName} {option.lastName}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              <strong>Timestamp:</strong> {timestamp}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {hasAssociation && (
              <>
                <Typography variant="body2">
                  <strong>{option.association.type}:</strong> {option.association.name}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </>
            )}
            {def && (
              <>
                <Typography variant="body2">
                  <strong>Definition:</strong> {def}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </>
            )}
            {comment && (
              <>
                <Typography variant="body2">
                  <strong>Comment:</strong> {comment}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </>
            )}
            {synonyms && (
              <Typography variant="body2">
                <strong>Synonyms:</strong> {synonyms}
              </Typography>
            )}
          </Box>
        )

        return (
          <ClickAwayTooltip key={index} title={tooltipContent} placement="top" interactive>
            <Chip label={option.label} {...getTagProps({ index })} onDelete={() => handleDelete(option)} />
          </ClickAwayTooltip>
        )
      })}
    </>
  )
}

export default Tags
